/**
 * Application shell: the primary navigation and the signed-in identity.
 *
 * Administration is hidden from a SALES user, but that is presentation only —
 * the server refuses those endpoints regardless of what the browser renders.
 */
import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui';

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/proposals/new', label: 'New Proposal', end: false },
  { to: '/templates', label: 'Templates', end: false },
  { to: '/administration', label: 'Administration', end: false, adminOnly: true },
];

export function AppLayout() {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = NAV.filter((item) => !item.adminOnly || hasRole('ADMIN'));

  const handleSignOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-white">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:ring-2 focus:ring-teal"
      >
        Skip to content
      </a>

      <header className="border-b border-rule bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-8">
            <NavLink to="/" className="flex items-center gap-2.5">
              <LotusMark />
              <span className="hidden text-[13px] font-semibold uppercase tracking-[0.08em] text-ink sm:block">
                Lotus Tech Proposals
              </span>
            </NavLink>

            <nav aria-label="Primary" className="hidden md:block">
              <ul className="flex items-center gap-1">
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        [
                          'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-tint text-teal'
                            : 'text-body hover:bg-tint hover:text-ink',
                        ].join(' ')
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden text-right sm:block">
                <p className="text-[13px] font-medium text-ink">{user.displayName}</p>
                <p className="text-[11px] uppercase tracking-wide text-muted">
                  {user.role}
                </p>
              </div>
            )}
            <Button variant="secondary" size="sm" onClick={handleSignOut}>
              Sign out
            </Button>
            <button
              type="button"
              className="rounded-md p-2 text-body md:hidden"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label="Toggle navigation"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav id="mobile-nav" aria-label="Primary" className="border-t border-rule md:hidden">
            <ul className="mx-auto max-w-7xl px-4 py-2 sm:px-6">
              {items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      [
                        'block rounded-md px-3 py-2 text-sm font-medium',
                        isActive
                          ? 'bg-tint text-teal'
                          : 'text-body hover:bg-tint',
                      ].join(' ')
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      <main id="main" className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <Outlet />
      </main>

      <footer className="mx-auto max-w-7xl px-4 pb-10 sm:px-6">
        <p className="border-t border-rule pt-4 text-[12px] text-muted">
          Lotus Tech Solutions Inc. — Confidential
        </p>
      </footer>
    </div>
  );
}

/** A small lotus mark, echoing the logo on the approved cover page. */
export function LotusMark({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path d="M16 5c2.6 2.9 3.9 5.9 3.9 9.1 0 3.1-1.3 6-3.9 8.6-2.6-2.6-3.9-5.5-3.9-8.6C12.1 10.9 13.4 7.9 16 5Z" fill="#1C6E8C" />
      <path d="M16 22.7c-2.9 0-5.5-1-7.7-3-1.4-1.3-2.4-2.8-3-4.5 2.4-.6 4.6-.4 6.6.7 2.2 1.2 3.6 3.4 4.1 6.8Z" fill="#2E9E6B" opacity=".85" />
      <path d="M16 22.7c2.9 0 5.5-1 7.7-3 1.4-1.3 2.4-2.8 3-4.5-2.4-.6-4.6-.4-6.6.7-2.2 1.2-3.6 3.4-4.1 6.8Z" fill="#2E9E6B" opacity=".85" />
      <path d="M5 25h22" stroke="#0E2439" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
